import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import QuoteForm from "./QuoteForm";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useI18n } from "../context/I18nContext";
import { useConfig } from "../context/ConfigContext";
import { submitContact } from "../lib/api";

/**
 * Standalone tabs + form section for /contact, extracted from ContactPage so
 * it can be appended to the CMS-driven contact rendering. Behaviour is
 * identical to the legacy implementation.
 */
export default function ContactFormSection() {
  const [params] = useSearchParams();
  const isQuote = params.get("get-quote") === "1";
  const [tab, setTab] = useState(isQuote ? "quote" : "message");
  const { t } = useI18n();
  const { address } = useConfig();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isQuote) return;
    // Phase 18.12 — scroll directly to the quote form so a CTA like
    // "Vraag offerte aan" lands the customer on the form itself, not
    // on the hubs/vestigingen section above. Retry briefly because
    // the cargo-info modal can hijack the initial scroll position.
    let attempts = 0;
    const tryScroll = () => {
      const target = document.getElementById("quote-form") ||
                     document.getElementById("contact-tabs");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      attempts += 1;
      if (attempts < 4) setTimeout(tryScroll, 350);
    };
    const t = setTimeout(tryScroll, 150);
    return () => clearTimeout(t);
  }, [isQuote]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.msg_required_error"));
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success(t("contact.msg_send_success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error(t("contact.msg_send_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact-tabs"
      className="py-20 bg-gradient-to-b from-slate-50 via-white to-slate-50"
      data-testid="contact-form-section"
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="flex gap-2 mb-6" data-testid="contact-tabs">
            <button
              onClick={() => setTab("quote")}
              data-testid="tab-quote"
              className={`px-5 py-2.5 rounded-xl font-chivo font-bold tracking-wide uppercase text-xs transition-all ${
                tab === "quote"
                  ? "bg-brand-900 text-white shadow-brand-soft"
                  : "bg-white text-brand-900 border border-slate-200 hover:border-brand-500"
              }`}
            >
              {t("contact.tab_quote")}
            </button>
            <button
              onClick={() => setTab("message")}
              data-testid="tab-message"
              className={`px-5 py-2.5 rounded-xl font-chivo font-bold tracking-wide uppercase text-xs transition-all ${
                tab === "message"
                  ? "bg-brand-900 text-white shadow-brand-soft"
                  : "bg-white text-brand-900 border border-slate-200 hover:border-brand-500"
              }`}
            >
              {t("contact.tab_message")}
            </button>
          </div>

          {tab === "quote" ? (
            <div id="quote-form" data-testid="quote-form-anchor">
              <QuoteForm />
            </div>
          ) : (
            <form onSubmit={onSubmit} className="card-premium p-8 space-y-5" data-testid="contact-form">
              <h3 className="font-chivo font-black text-2xl text-brand-900">{t("contact.msg_title")}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="label-saas">{t("contact.msg_name")}</Label>
                  <Input data-testid="contact-input-name" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-xl" />
                </div>
                <div>
                  <Label className="label-saas">{t("contact.msg_email")}</Label>
                  <Input data-testid="contact-input-email" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="rounded-xl" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="label-saas">{t("contact.msg_phone")}</Label>
                  <Input data-testid="contact-input-phone" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="rounded-xl" />
                </div>
                <div>
                  <Label className="label-saas">{t("contact.msg_subject")}</Label>
                  <Input data-testid="contact-input-subject" value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="rounded-xl" />
                </div>
              </div>
              <div>
                <Label className="label-saas">{t("contact.msg_message")}</Label>
                <Textarea data-testid="contact-input-message" required rows={5} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="rounded-xl" />
              </div>
              <Button data-testid="contact-submit-button" type="submit" disabled={loading}
                className="w-full bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-chivo font-bold uppercase text-sm py-6 shadow-brand-soft hover:shadow-brand-glow active:scale-[0.98] transition-all">
                {loading ? t("contact.msg_sending") : t("contact.msg_submit")}
              </Button>
            </form>
          )}
        </div>

        <div>
          <h3 className="font-chivo font-black text-2xl text-brand-900 mb-5">{t("contact.visit_title")}</h3>
          <div className="card-premium p-7">
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center">
                <MapPin className="w-5 h-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="font-chivo font-bold text-brand-900">{t("contact.company_name")}</div>
                <div className="text-sm text-ink-500 mt-1" data-testid="contact-address">{address}</div>
              </div>
            </div>
            <div className="mt-6 aspect-[4/3] w-full overflow-hidden rounded-xl border border-slate-200">
              <iframe
                title="5999Cargo Rotterdam location"
                src="https://www.google.com/maps?q=Steenhouwerstraat+88,+3194+AG+Rotterdam&output=embed"
                className="w-full h-full" loading="lazy" data-testid="contact-map"
              />
            </div>
            <div className="mt-6 pt-6 border-t border-slate-100">
              <h4 className="font-chivo font-bold text-brand-900">{t("contact.dropoff_title")}</h4>
              <p className="text-sm text-ink-500 mt-2 leading-relaxed">{t("contact.dropoff_desc")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
